<script>
import { cloneDeep } from 'lodash-es';
import NonDynamicTransportTripInformation from './NonDynamicTransportTripInformation';

export default {
  name: 'NonDynamicTransportTripRow',
  components: {
    NonDynamicTransportTripInformation,
  },
  props: {
    transits: {
      type: Array,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    trip: {
      type: Object,
      required: true,
    },
    defaultTripSeating: {
      type: Object,
      required: true,
    },
    defaultTripConsumables: {
      type: Object,
      required: true,
    },
    defaultTripUpgrade: {
      type: Object,
      required: true,
    },
    carriers: {
      type: Array,
      default: () => [],
    },
    tripUpgradeTypes: {
      type: Array,
      required: true,
    },
    tripConsumableTypes: {
      type: Array,
      required: true,
    },
    tripSeatingSectionTypes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localTrip: null,
      localCarriers: null,
      selectedCarrier: null,
    };
  },
  created() {
    this.localTrip = cloneDeep(this.trip);
    this.localCarriers = this.carriers.map(carrier => carrier.name);
    if (this.trip.carrier.id) {
      this.carriers.filter((carrier) => {
        if (carrier.id === this.trip.carrier.id) {
          this.selectedCarrier = carrier.name;
        }
        return null;
      });
    }
  },
  methods: {
    filterTransits(item, queryText) {
      return (
        item.name.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1 ||
        item.iataCode.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1
      );
    },
    update(attribute, value) {
      this.$emit('update', { index: this.index, attribute, value });
    },
    updateDepartureTransit(event) {
      if (typeof (event) !== 'undefined') {
        this.update('departureTransit', { id: event.id, name: event.name });
      } else {
        this.update('departureTransit', { id: null, name: null });
      }
    },
    updateArrivalTransit(event) {
      if (typeof (event) !== 'undefined') {
        this.update('arrivalTransit', { id: event.id, name: event.name });
      } else {
        this.update('arrivalTransit', { id: null, name: null });
      }
    },
    updateDepartureTime(event) {
      return event.length ? this.update('departureTime', event) : this.update('departureTime', null);
    },
    updateArrivalTime(event) {
      if (event === '') {
        this.update('arrivalTime', null);
        this.update('offsetDays', null);
      } else {
        const values = event.split(' +');
        this.update('arrivalTime', values[0]);
        if (values[1] !== '') {
          this.update('offsetDays', values[1]);
        } else {
          this.update('offsetDays', null);
        }
      }
    },
    updateRef(event) {
      return event.length ? this.update('ref', event) : this.update('ref', null);
    },
    updateCarrier(event) {
      if (!event) {
        this.update('carrier', { id: null, name: null });
      } else {
        const found = this.carriers.find(e => e.name === event);
        if (found) {
          this.update('carrier', { id: found.id, name: found.name });
        } else {
          this.update('carrier', { id: null, name: event });
        }
      }
    },
  },
  computed: {
    arrivalTime() {
      const offsetDays = this.trip.offsetDays ? ` +${this.trip.offsetDays}` : '+';
      return `${this.trip.arrivalTime}${offsetDays}`;
    },
  },
};
</script>

<template>
  <v-layout row>
    <v-flex xs4>
      <v-autocomplete
        :items="transits"
        :value="trip.departureTransit.id"
        :filter="filterTransits"
        :label="$t('nonDynamicTransport.labels.departureAirport')"
        class="mr-2"
        item-text="name"
        item-value="id"
        solo
        return-object
        clearable
        @change="updateDepartureTransit"
      >
        <template v-slot:selection='data'>
          {{ data.item.name }} - ({{ data.item.iataCode }})
        </template>
        <template v-slot:item='data'>
          <v-list-tile-content>
            <v-list-tile-title>
              {{ data.item.name }} - ({{ data.item.iataCode }})
            </v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-autocomplete>
    </v-flex>
    <v-flex xs1>
      <v-text-field
        :value="trip.departureTime"
        :label="$t('nonDynamicTransport.labels.departureTime')"
        class="mr-2"
        solo
        return-masked-value
        mask="##:##"
        @input="updateDepartureTime"
      />
    </v-flex>
    <v-flex xs4>
      <v-autocomplete
        :value="trip.arrivalTransit.id"
        :items="transits"
        :label="$t('nonDynamicTransport.labels.arrivalAirport')"
        :filter="filterTransits"
        class="mr-2"
        item-text="name"
        item-value="id"
        solo
        clearable
        return-object
        @change="updateArrivalTransit"
      >
        <template v-slot:selection='data'>
          {{ data.item.name }} - ({{ data.item.iataCode }})
        </template>
        <template v-slot:item='data'>
          <v-list-tile-content>
            <v-list-tile-title>
              {{ data.item.name }} - ({{ data.item.iataCode }})
            </v-list-tile-title>
          </v-list-tile-content>
        </template>
      </v-autocomplete>
    </v-flex>
    <v-flex xs1>
      <v-text-field
        :value="arrivalTime"
        :label="$t('nonDynamicTransport.labels.arrivalTime')"
        class="mr-2"
        solo
        mask="##:## +#"
        return-masked-value
        @input="updateArrivalTime"
      />
    </v-flex>
    <v-flex xs2>
      <v-combobox
        :value="selectedCarrier"
        :items="localCarriers"
        :label="$t('nonDynamicTransport.labels.carrier')"
        class="mr-2"
        item-value="name"
        item-text="name"
        solo
        small-chips
        return-object
        @change="updateCarrier"
      >
        <template v-slot:no-data>
          <v-list-tile>
            <v-list-tile-content>
              <v-list-tile-title
                v-html="$t('nonDynamicTransport.labels.noResult')"
              />
            </v-list-tile-content>
          </v-list-tile>
        </template>
      </v-combobox>
    </v-flex>
    <v-flex xs1>
      <v-text-field
        :value="trip.ref"
        :label="$t('nonDynamicTransport.labels.ref')"
        class="mr-2"
        solo
        @input="updateRef"
      />
    </v-flex>
    <v-flex>
      <non-dynamic-transport-trip-information
        :trip-id="trip.id"
        :trip-upgrade-types="tripUpgradeTypes"
        :trip-consumable-types="tripConsumableTypes"
        :trip-seating-section-types="tripSeatingSectionTypes"
        :default-trip-seating="defaultTripSeating"
        :default-trip-consumables="defaultTripConsumables"
        :default-trip-upgrade="defaultTripUpgrade"
      />
    </v-flex>
    <v-flex xs1>
      <v-icon
        class="mt-2"
        color="success"
        size="30"
        @click="$emit('add', index)"
      >
        add_box
      </v-icon>
      <v-icon
        color="red"
        size="30"
        @click="$emit('remove', index)"
      >
        delete
      </v-icon>
    </v-flex>
  </v-layout>
</template>

<style lang="scss" scoped>
  @import '../../assets/scss/variables';

  /deep/ .v-text-field .v-label {
    font-size: $font-size-md - 2px;
  }

  /deep/ .v-text-field {
    font-size: $font-size-md - 2px;
  }
</style>



// WEBPACK FOOTER //
// src/components/non-dynamic-transport/NonDynamicTransportTripRow.vue